import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import ExcelImportDialog from '@/components/ExcelImportDialog';
import InvoiceImportDialog from '@/components/InvoiceImportDialog';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';
import { Receipt, Plus, Trash2, Search, FileText, Building, Pencil, Eye, Filter, CheckCircle, Clock, X, Loader2, Upload, ChevronDown, FileSpreadsheet, FileCode } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Expense categories with Italian labels
const EXPENSE_CATEGORIES = {
  ENERGIA: 'Energia elettrica',
  ACQUA: 'Acqua',
  GAS: 'Gas',
  RISCALDAMENTO: 'Riscaldamento',
  MANUTENZIONE_ORDINARIA: 'Manutenzione ordinaria',
  MANUTENZIONE_STRAORDINARIA: 'Manutenzione straordinaria',
  PULIZIA: 'Pulizia',
  ASSICURAZIONE: 'Assicurazione',
  AMMINISTRAZIONE: 'Amministrazione',
  COMPENSO_AMMINISTRATORE: 'Compenso amministratore',
  PORTIERE: 'Portiere',
  ASCENSORE: 'Ascensore',
  SPESE_LEGALI: 'Spese legali',
  SPESE_BANCARIE: 'Spese bancarie',
  ALTRO: 'Altro'
};

// Payment methods with Italian labels
const PAYMENT_METHODS = {
  BONIFICO: 'Bonifico bancario',
  ASSEGNO: 'Assegno',
  CONTANTI: 'Contanti',
  RID: 'RID (Addebito diretto)',
  BANCOMAT: 'Bancomat',
  CARTA_CREDITO: 'Carta di credito',
  ALTRO: 'Altro'
};

// Expense status with Italian labels
const EXPENSE_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800', icon: FileText },
  PENDING: { label: 'Da pagare', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  APPROVED: { label: 'Approvata', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  PAID: { label: 'Pagata', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  CANCELLED: { label: 'Annullata', color: 'bg-red-100 text-red-800', icon: X }
};

// Standard Italian rates
const VAT_RATES = [
  { value: 22, label: '22% (Ordinaria)' },
  { value: 10, label: '10% (Ridotta)' },
  { value: 4, label: '4% (Minima)' },
  { value: 0, label: '0% (Esente)' }
];

const WITHHOLDING_RATES = [
  { value: 0, label: 'Nessuna ritenuta' },
  { value: 4, label: '4% (Condomini - pulizie, manutenzione)' },
  { value: 20, label: '20% (Professionisti)' },
  { value: 23, label: '23% (Altro)' }
];

export const Expenses = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const canEdit = ['ADMIN', 'ACCOUNTANT'].includes(userRole);
  
  // Data state
  const [expenses, setExpenses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isSupplierDialogOpen, setIsSupplierDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [isImportExpensesOpen, setIsImportExpensesOpen] = useState(false);
  const [isImportSuppliersOpen, setIsImportSuppliersOpen] = useState(false);
  const [isImportPdfOpen, setIsImportPdfOpen] = useState(false);
  const [isImportXmlOpen, setIsImportXmlOpen] = useState(false);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  
  // Forms
  const [expenseForm, setExpenseForm] = useState({
    supplier_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    description: '',
    category: 'ALTRO',
    taxable_amount: '',
    vat_rate: 22,
    withholding_applicable: false,
    withholding_rate: 0,
    withholding_base_rule: 'TAXABLE_ONLY',
    due_date: '',
    payment_date: '',
    payment_method: '',
    status: 'PENDING',
    notes: ''
  });

  const [supplierForm, setSupplierForm] = useState({
    name: '',
    vat_number: '',
    fiscal_code: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    iban: '',
    supplier_type: 'COMPANY',
    withholding_applicable: false,
    default_vat_rate: 22,
    default_withholding_rate: 0,
    withholding_category: 'OTHER',
    notes: ''
  });

  // Fetch expenses
  const fetchExpenses = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setExpenses(await res.json());
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  }, [condoId, token]);

  // Fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSuppliers(await res.json());
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, [condoId, token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchExpenses(), fetchSuppliers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchExpenses, fetchSuppliers]);

  // Calculate expense amounts
  const calculateExpenseAmounts = (taxable, vatRate, withholdingRate, withholdingApplicable, baseRule = 'TAXABLE_ONLY') => {
    const t = parseFloat(taxable) || 0;
    const vat = Math.round(t * vatRate / 100 * 100) / 100;
    const total = Math.round((t + vat) * 100) / 100;
    
    let withholdingBase = t;
    if (baseRule === 'GROSS_TOTAL') {
      withholdingBase = total;
    }
    
    const withholding = (withholdingApplicable && withholdingRate > 0) 
      ? Math.round(withholdingBase * withholdingRate / 100 * 100) / 100 
      : 0;
    const netToPay = Math.round((total - withholding) * 100) / 100;
    return { vat, total, withholding, netToPay, withholdingBase };
  };

  // Reset forms
  const resetExpenseForm = () => {
    setExpenseForm({
      supplier_id: '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      description: '',
      category: 'ALTRO',
      taxable_amount: '',
      vat_rate: 22,
      withholding_applicable: false,
      withholding_rate: 0,
      withholding_base_rule: 'TAXABLE_ONLY',
      due_date: '',
      payment_date: '',
      payment_method: '',
      status: 'PENDING',
      notes: ''
    });
    setEditingExpense(null);
  };

  const resetSupplierForm = () => {
    setSupplierForm({
      name: '',
      vat_number: '',
      fiscal_code: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      province: '',
      iban: '',
      supplier_type: 'COMPANY',
      withholding_applicable: false,
      default_vat_rate: 22,
      default_withholding_rate: 0,
      withholding_category: 'OTHER',
      notes: ''
    });
  };

  // Create/Update expense
  const handleSaveExpense = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const calculated = calculateExpenseAmounts(
        expenseForm.taxable_amount,
        expenseForm.vat_rate,
        expenseForm.withholding_rate,
        expenseForm.withholding_applicable,
        expenseForm.withholding_base_rule
      );
      
      const payload = {
        ...expenseForm,
        taxable_amount: parseFloat(expenseForm.taxable_amount),
        vat_amount: calculated.vat,
        total: calculated.total,
        withholding_base_amount: calculated.withholdingBase,
        withholding_amount: calculated.withholding,
        net_to_pay: calculated.netToPay
      };
      
      const url = editingExpense 
        ? `${API_URL}/api/condominiums/${condoId}/expenses/${editingExpense.id}`
        : `${API_URL}/api/condominiums/${condoId}/expenses`;
      
      const res = await fetch(url, {
        method: editingExpense ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(editingExpense ? 'Spesa aggiornata' : 'Spesa registrata con successo');
        setIsExpenseDialogOpen(false);
        resetExpenseForm();
        fetchExpenses();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella registrazione');
      }
    } catch (error) {
      toast.error('Errore nella registrazione');
    } finally {
      setSaving(false);
    }
  };

  // Create supplier
  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/suppliers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(supplierForm)
      });
      
      if (res.ok) {
        const newSupplier = await res.json();
        toast.success('Fornitore creato');
        setIsSupplierDialogOpen(false);
        resetSupplierForm();
        fetchSuppliers();
        // Auto-select new supplier in expense form
        setExpenseForm(prev => ({ ...prev, supplier_id: newSupplier.id }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella creazione');
      }
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  // Delete expense
  const handleDeleteExpense = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questa spesa?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/expenses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Spesa eliminata');
        fetchExpenses();
      } else {
        toast.error('Errore nella cancellazione');
      }
    } catch (error) {
      toast.error('Errore nella cancellazione');
    }
  };

  // Open edit dialog
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      supplier_id: expense.supplier_id || '',
      invoice_number: expense.invoice_number || '',
      invoice_date: expense.invoice_date?.split('T')[0] || '',
      description: expense.description || '',
      category: expense.category || 'ALTRO',
      taxable_amount: expense.taxable_amount?.toString() || '',
      vat_rate: expense.vat_rate || 22,
      withholding_applicable: expense.withholding_applicable || false,
      withholding_rate: expense.withholding_rate || 0,
      withholding_base_rule: expense.withholding_base_rule || 'TAXABLE_ONLY',
      due_date: expense.due_date?.split('T')[0] || '',
      payment_date: expense.payment_date?.split('T')[0] || '',
      payment_method: expense.payment_method || '',
      status: expense.status || 'PENDING',
      notes: expense.notes || ''
    });
    setIsExpenseDialogOpen(true);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-IT');
  };

  // Get supplier name
  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.name || '-';
  };

  // Live calculation preview
  const previewAmounts = calculateExpenseAmounts(
    expenseForm.taxable_amount,
    expenseForm.vat_rate,
    expenseForm.withholding_rate,
    expenseForm.withholding_applicable,
    expenseForm.withholding_base_rule
  );

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = !searchTerm || 
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getSupplierName(expense.supplier_id).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'ALL' || expense.status === statusFilter;
    const matchesCategory = categoryFilter === 'ALL' || expense.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Summary stats
  const stats = {
    total: filteredExpenses.reduce((sum, e) => sum + (e.total || 0), 0),
    pending: filteredExpenses.filter(e => e.status === 'PENDING').reduce((sum, e) => sum + (e.total || 0), 0),
    paid: filteredExpenses.filter(e => e.status === 'PAID').reduce((sum, e) => sum + (e.total || 0), 0),
    count: filteredExpenses.length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Spese e Fatture" subtitle="Registrazione spese e allocazioni" />
      {canEdit && (
        <div className="flex flex-wrap gap-2 justify-end">
            {/* Import Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Importa
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setIsImportExpensesOpen(true)}>
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                  Fatture da Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsImportPdfOpen(true)}>
                  <FileText className="h-4 w-4 mr-2 text-red-600" />
                  Fattura da PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsImportXmlOpen(true)}>
                  <FileCode className="h-4 w-4 mr-2 text-orange-600" />
                  Fattura XML (FatturaPA)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsImportSuppliersOpen(true)}>
                  <Building className="h-4 w-4 mr-2" />
                  Fornitori da Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button 
              onClick={() => { resetExpenseForm(); setIsExpenseDialogOpen(true); }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuova Spesa
            </Button>
          </div>
        )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Totale Spese</div>
            <div className="text-xl font-bold">{formatCurrency(stats.total)}</div>
            <div className="text-xs text-muted-foreground">{stats.count} fatture</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Da Pagare</div>
            <div className="text-xl font-bold text-yellow-600">{formatCurrency(stats.pending)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Pagate</div>
            <div className="text-xl font-bold text-green-600">{formatCurrency(stats.paid)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Fornitori</div>
            <div className="text-xl font-bold">{suppliers.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per descrizione, numero fattura o fornitore..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutti gli stati</SelectItem>
                {Object.entries(EXPENSE_STATUS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutte le categorie</SelectItem>
                {Object.entries(EXPENSE_CATEGORIES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Spese</CardTitle>
          <CardDescription>
            {filteredExpenses.length} spese trovate
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Nessuna spesa trovata</p>
              <p className="text-sm">
                {expenses.length === 0 
                  ? 'Inizia registrando la prima fattura' 
                  : 'Prova a modificare i filtri di ricerca'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>N. Fattura</TableHead>
                    <TableHead>Fornitore</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-right">Totale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => {
                    const statusInfo = EXPENSE_STATUS[expense.status] || EXPENSE_STATUS.PENDING;
                    return (
                      <TableRow key={expense.id}>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(expense.invoice_date)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {expense.invoice_number || '-'}
                        </TableCell>
                        <TableCell>{getSupplierName(expense.supplier_id)}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {expense.description || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {EXPENSE_CATEGORIES[expense.category] || expense.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(expense.total)}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusInfo.color}>
                            {statusInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setViewingExpense(expense)}
                              title="Visualizza"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canEdit && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleEditExpense(expense)}
                                  title="Modifica"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDeleteExpense(expense.id)}
                                  className="text-red-600"
                                  title="Elimina"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New/Edit Expense Dialog */}
      <Dialog open={isExpenseDialogOpen} onOpenChange={setIsExpenseDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? 'Modifica Spesa' : 'Registra Nuova Spesa'}
            </DialogTitle>
            <DialogDescription>
              Registra una fattura fornitore con calcolo automatico IVA e ritenuta
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveExpense} className="space-y-4">
            {/* Supplier Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fornitore</Label>
                <div className="flex gap-2">
                  <Select
                    value={expenseForm.supplier_id}
                    onValueChange={(v) => {
                      const supplier = suppliers.find(s => s.id === v);
                      const hasWithholding = supplier?.withholding_applicable || (supplier?.default_withholding_rate > 0);
                      setExpenseForm({
                        ...expenseForm,
                        supplier_id: v,
                        vat_rate: supplier?.default_vat_rate || 22,
                        withholding_applicable: hasWithholding,
                        withholding_rate: hasWithholding ? (supplier?.default_withholding_rate || 4) : 0
                      });
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Seleziona fornitore..." />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => setIsSupplierDialogOpen(true)}
                    title="Nuovo fornitore"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select
                  value={expenseForm.category}
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Invoice Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>N. Fattura</Label>
                <Input
                  value={expenseForm.invoice_number}
                  onChange={(e) => setExpenseForm({ ...expenseForm, invoice_number: e.target.value })}
                  placeholder="Es: 2024/001"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fattura *</Label>
                <Input
                  type="date"
                  value={expenseForm.invoice_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, invoice_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Scadenza</Label>
                <Input
                  type="date"
                  value={expenseForm.due_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, due_date: e.target.value })}
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="Descrizione della spesa..."
                rows={2}
              />
            </div>

            <Separator />

            {/* Amounts */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Imponibile (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={expenseForm.taxable_amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, taxable_amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Aliquota IVA</Label>
                <Select
                  value={expenseForm.vat_rate.toString()}
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, vat_rate: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VAT_RATES.map((r) => (
                      <SelectItem key={r.value} value={r.value.toString()}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>IVA Calcolata</Label>
                <Input
                  value={formatCurrency(previewAmounts.vat)}
                  disabled
                  className="bg-gray-50"
                />
              </div>
            </div>

            {/* Withholding */}
            <div className="p-4 bg-amber-50 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>Ritenuta d&#39;acconto</Label>
                </div>
                <Switch
                  checked={expenseForm.withholding_applicable}
                  onCheckedChange={(v) => setExpenseForm({ 
                    ...expenseForm, 
                    withholding_applicable: v,
                    withholding_rate: v ? 4 : 0
                  })}
                />
              </div>
              
              {expenseForm.withholding_applicable && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Aliquota Ritenuta</Label>
                    <Select
                      value={expenseForm.withholding_rate.toString()}
                      onValueChange={(v) => setExpenseForm({ ...expenseForm, withholding_rate: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WITHHOLDING_RATES.filter(r => r.value > 0).map((r) => (
                          <SelectItem key={r.value} value={r.value.toString()}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ritenuta Calcolata</Label>
                    <Input
                      value={formatCurrency(previewAmounts.withholding)}
                      disabled
                      className="bg-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Totals Preview */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-xs text-muted-foreground">Totale Fattura</div>
                  <div className="text-lg font-bold">{formatCurrency(previewAmounts.total)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Ritenuta</div>
                  <div className="text-lg font-bold text-amber-600">- {formatCurrency(previewAmounts.withholding)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Netto da Pagare</div>
                  <div className="text-lg font-bold text-green-600">{formatCurrency(previewAmounts.netToPay)}</div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Payment & Status */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select
                  value={expenseForm.status}
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPENSE_STATUS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Metodo Pagamento</Label>
                <Select
                  value={expenseForm.payment_method}
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data Pagamento</Label>
                <Input
                  type="date"
                  value={expenseForm.payment_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, payment_date: e.target.value })}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsExpenseDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingExpense ? 'Aggiorna' : 'Registra Spesa'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Supplier Dialog */}
      <Dialog open={isSupplierDialogOpen} onOpenChange={setIsSupplierDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Fornitore</DialogTitle>
            <DialogDescription>
              Aggiungi un nuovo fornitore all&#39;anagrafica
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSupplier} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome/Ragione Sociale *</Label>
              <Input
                value={supplierForm.name}
                onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                placeholder="Es: Impresa Pulizie Rossi Srl"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>P.IVA</Label>
                <Input
                  value={supplierForm.vat_number}
                  onChange={(e) => setSupplierForm({ ...supplierForm, vat_number: e.target.value })}
                  placeholder="IT12345678901"
                />
              </div>
              <div className="space-y-2">
                <Label>Codice Fiscale</Label>
                <Input
                  value={supplierForm.fiscal_code}
                  onChange={(e) => setSupplierForm({ ...supplierForm, fiscal_code: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>IBAN</Label>
              <Input
                value={supplierForm.iban}
                onChange={(e) => setSupplierForm({ ...supplierForm, iban: e.target.value })}
                placeholder="IT..."
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div>
                <Label>Soggetto a ritenuta d&#39;acconto</Label>
                <p className="text-xs text-muted-foreground">Applica automaticamente ritenuta alle fatture</p>
              </div>
              <Switch
                checked={supplierForm.withholding_applicable}
                onCheckedChange={(v) => setSupplierForm({ 
                  ...supplierForm, 
                  withholding_applicable: v,
                  default_withholding_rate: v ? 4 : 0
                })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsSupplierDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit">Crea Fornitore</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Expense Dialog */}
      <Dialog open={!!viewingExpense} onOpenChange={() => setViewingExpense(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettaglio Spesa</DialogTitle>
          </DialogHeader>
          {viewingExpense && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">N. Fattura</div>
                  <div className="font-medium">{viewingExpense.invoice_number || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Data</div>
                  <div className="font-medium">{formatDate(viewingExpense.invoice_date)}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Fornitore</div>
                <div className="font-medium">{getSupplierName(viewingExpense.supplier_id)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Descrizione</div>
                <div className="font-medium">{viewingExpense.description || '-'}</div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">Imponibile</div>
                  <div className="font-medium">{formatCurrency(viewingExpense.taxable_amount)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">IVA ({viewingExpense.vat_rate}%)</div>
                  <div className="font-medium">{formatCurrency(viewingExpense.vat_amount)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Totale</div>
                  <div className="font-bold text-lg">{formatCurrency(viewingExpense.total)}</div>
                </div>
                {viewingExpense.withholding_amount > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground">Ritenuta ({viewingExpense.withholding_rate}%)</div>
                    <div className="font-medium text-amber-600">- {formatCurrency(viewingExpense.withholding_amount)}</div>
                  </div>
                )}
              </div>
              {viewingExpense.withholding_amount > 0 && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Netto da Pagare</div>
                  <div className="font-bold text-lg text-green-600">{formatCurrency(viewingExpense.net_to_pay)}</div>
                </div>
              )}
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">Stato</div>
                  <Badge className={EXPENSE_STATUS[viewingExpense.status]?.color}>
                    {EXPENSE_STATUS[viewingExpense.status]?.label}
                  </Badge>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Categoria</div>
                  <div className="font-medium">{EXPENSE_CATEGORIES[viewingExpense.category]}</div>
                </div>
              </div>
              {viewingExpense.notes && (
                <div>
                  <div className="text-xs text-muted-foreground">Note</div>
                  <div className="text-sm">{viewingExpense.notes}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingExpense(null)}>
              Chiudi
            </Button>
            {canEdit && (
              <Button onClick={() => {
                handleEditExpense(viewingExpense);
                setViewingExpense(null);
              }}>
                <Pencil className="h-4 w-4 mr-2" />
                Modifica
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Expenses Dialog */}
      <ExcelImportDialog
        open={isImportExpensesOpen}
        onOpenChange={setIsImportExpensesOpen}
        importType="expenses"
        condominiumId={condoId}
        onSuccess={() => {
          fetchExpenses();
          fetchSuppliers();
        }}
      />

      {/* Import Suppliers Dialog */}
      <ExcelImportDialog
        open={isImportSuppliersOpen}
        onOpenChange={setIsImportSuppliersOpen}
        importType="suppliers"
        condominiumId={condoId}
        onSuccess={() => {
          fetchSuppliers();
        }}
      />

      {/* Import Invoice from PDF Dialog */}
      <InvoiceImportDialog
        open={isImportPdfOpen}
        onOpenChange={setIsImportPdfOpen}
        importType="pdf"
        condominiumId={condoId}
        onSuccess={() => {
          fetchExpenses();
          fetchSuppliers();
        }}
      />

      {/* Import Invoice from XML Dialog */}
      <InvoiceImportDialog
        open={isImportXmlOpen}
        onOpenChange={setIsImportXmlOpen}
        importType="xml"
        condominiumId={condoId}
        onSuccess={() => {
          fetchExpenses();
          fetchSuppliers();
        }}
      />
    </div>
  );
};

export default Expenses;
